import React, { useEffect, useState, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  loadMasteredSnippetsFromCacheOrLegacy,
  refreshAchievementStatsCacheCallable
} from '../../services/profileAchievementStatsService';
import {
  SNIPPET_MASTERY_EMPTY_HINT,
  SNIPPET_MASTERY_TOOLTIP
} from '../../constants/snippetMasteryCopy';
import IconRenderer from '../ui/IconRenderer';
import { ReactComponent as UserProfileMasteredSnippetsListLibrarySnippetIcon } from '../../Icons/deck-icon.svg';
import { UserProfileMasteredSnippetsListSkeleton } from './UserProfilePanelSkeletons';
import './UserProfileMasteredSnippetsList.css';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const PAGE_SIZE_STORAGE_KEY = 'flashcode-profile-mastered-snippets-page-size';

function readStoredPageSize() {
  if (typeof window === 'undefined') return 10;
  try {
    const raw = sessionStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    const n = parseInt(raw, 10);
    if (PAGE_SIZE_OPTIONS.includes(n)) return n;
  } catch {
    /* ignore */
  }
  return 10;
}

function languageIconNameFromKey(langKey) {
  if (!langKey) return 'javascript';
  const k = String(langKey).trim().toLowerCase();
  if (k === 'c++' || k === 'cpp' || k === 'cplusplus') return 'cpp';
  if (k === 'c#' || k === 'csharp') return 'csharp';
  return k.replace(/\s+/g, '') || 'javascript';
}

function languageDisplayLabel(key) {
  if (!key || key === 'other') return 'Other';
  const map = {
    javascript: 'JavaScript',
    typescript: 'TypeScript',
    python: 'Python',
    java: 'Java',
    cpp: 'C++',
    csharp: 'C#'
  };
  const k = String(key).trim().toLowerCase();
  return map[k] || key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * Profile achievements: list cookbook cards that pass isCardMastered (same rule as roadmap “mastered”).
 */
export default function UserProfileMasteredSnippetsList({ userId, languageFilter, canView }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(readStoredPageSize);
  const [currentPage, setCurrentPage] = useState(1);
  /** One background cache warmup per language scope when list was not in profile_stats_cache. */
  const cacheWarmupAttempted = useRef(false);

  useEffect(() => {
    cacheWarmupAttempted.current = false;
  }, [languageFilter, userId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [languageFilter, userId]);

  const { paginatedItems, totalPages, rangeStart, rangeEnd } = useMemo(() => {
    const total = items.length;
    const tp = Math.max(1, Math.ceil(total / pageSize));
    const start = (currentPage - 1) * pageSize;
    const slice = items.slice(start, start + pageSize);
    const rs = total === 0 ? 0 : start + 1;
    const re = Math.min(start + pageSize, total);
    return { paginatedItems: slice, totalPages: tp, rangeStart: rs, rangeEnd: re };
  }, [items, currentPage, pageSize]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(items.length / pageSize));
    setCurrentPage((p) => Math.min(Math.max(1, p), maxPage));
  }, [items, pageSize]);

  const handlePageSizeChange = (e) => {
    const n = parseInt(e.target.value, 10);
    if (!PAGE_SIZE_OPTIONS.includes(n)) return;
    setPageSize(n);
    setCurrentPage(1);
    try {
      sessionStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(n));
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!userId || !canView) {
      setItems([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { items: list, needsCacheWarmup } = await loadMasteredSnippetsFromCacheOrLegacy(
          userId,
          languageFilter || ''
        );
        if (!cancelled) setItems(list);
        if (
          needsCacheWarmup &&
          !cacheWarmupAttempted.current &&
          !cancelled
        ) {
          cacheWarmupAttempted.current = true;
          refreshAchievementStatsCacheCallable().catch(() => {
            /* Backfill on next visit; fails if functions not deployed */
          });
        }
      } catch (e) {
        console.error('UserProfileMasteredSnippetsList: load failed', e);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, canView, languageFilter]);

  if (!canView) {
    return (
      <p className="user-profile-mastered-snippets-list__private">
        Snippet mastery is only visible on your own profile.
      </p>
    );
  }

  if (loading) {
    return <UserProfileMasteredSnippetsListSkeleton />;
  }

  if (items.length === 0) {
    const lang =
      languageFilter && languageFilter !== 'other' ? languageDisplayLabel(languageFilter) : null;
    return (
      <div className="user-profile-mastered-snippets-list user-profile-mastered-snippets-list--empty">
        <p className="user-profile-mastered-snippets-list__empty-text">
          {lang ? (
            <>
              No snippets mastered in{' '}
              <span className="user-profile-mastered-snippets-list__empty-lang">{lang}</span> yet.{' '}
              {SNIPPET_MASTERY_EMPTY_HINT}
            </>
          ) : (
            <>No snippets mastered yet. {SNIPPET_MASTERY_EMPTY_HINT}</>
          )}
        </p>
      </div>
    );
  }

  return (
    <div
      className="user-profile-mastered-snippets-list"
      role="region"
      aria-label="Mastered snippets"
      title={SNIPPET_MASTERY_TOOLTIP}
    >
      {/* <p className="user-profile-mastered-snippets-list__intro">
        Cookbook cards at ≥90% SRS success rate
        {languageFilter ? ` · ${languageDisplayLabel(languageFilter)}` : ''}.
      </p> */}
      <ul className="user-profile-mastered-snippets-list__items">
        {paginatedItems.map((row) => (
          <li key={`${row.deckId}_${row.cardId}`} className="user-profile-mastered-snippets-list__item">
            <div className="user-profile-mastered-snippets-list__body">
              <div className="user-profile-mastered-snippets-list__title-row">
                {/* Language icon only (no lang pill); deck row uses library snippet icon */}
                <IconRenderer
                  name={languageIconNameFromKey(row.language)}
                  type="language"
                  size="small"
                  className="user-profile-mastered-snippets-list__title-lang-icon"
                  aria-hidden
                />
                <h3 className="user-profile-mastered-snippets-list__title">{row.snippetTitle}</h3>
              </div>
              <div className="user-profile-mastered-snippets-list__deck-row">
                <UserProfileMasteredSnippetsListLibrarySnippetIcon
                  className="user-profile-mastered-snippets-list__deck-snippet-icon"
                  aria-hidden
                />
                <p className="user-profile-mastered-snippets-list__deck">{row.deckName}</p>
              </div>
            </div>
          </li>
        ))}
      </ul>
      <footer className="user-profile-mastered-snippets-list__footer">
        <div className="user-profile-mastered-snippets-list__footer-inner">
          <div className="user-profile-mastered-snippets-list__footer-size">
            <label
              htmlFor="user-profile-mastered-snippets-list-page-size"
              className="user-profile-mastered-snippets-list__footer-label"
            >
              Per page
            </label>
            <select
              id="user-profile-mastered-snippets-list-page-size"
              className="user-profile-mastered-snippets-list__footer-select"
              value={pageSize}
              onChange={handlePageSizeChange}
              aria-label="Snippets per page"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <nav
            className="user-profile-mastered-snippets-list__footer-nav"
            aria-label="Mastered snippets pages"
          >
            <button
              type="button"
              className="user-profile-mastered-snippets-list__footer-nav-btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              <ChevronLeft className="user-profile-mastered-snippets-list__footer-nav-icon" strokeWidth={2} />
            </button>
            <span className="user-profile-mastered-snippets-list__footer-page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="user-profile-mastered-snippets-list__footer-nav-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              aria-label="Next page"
            >
              <ChevronRight className="user-profile-mastered-snippets-list__footer-nav-icon" strokeWidth={2} />
            </button>
          </nav>
          <p className="user-profile-mastered-snippets-list__footer-range" aria-live="polite">
            Showing {rangeStart}–{rangeEnd} of {items.length}
          </p>
        </div>
      </footer>
    </div>
  );
}
